import { useState, useCallback, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { azureMapsProxy } from '@/lib/azure/azureMapsProxy';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useTranslation } from '@/hooks/useTranslation';
import { useDebounce } from '@/hooks/useDebounce';
import { COUNTRY_SEARCH_BIAS, isSupportedCountryCode } from '@/lib/countries';
import { formatPostalAddress, splitStreetLine } from '@/lib/addressFormat';
import type { Geometry } from 'geojson';
import type {
  AddressValidation,
  GeocodeResult,
  SearchAddressOptions,
  GisLocation,
  AzureAddress,
} from '@/lib/types/azure';

interface UseAddressLookupOptions {
  autoValidate?: boolean;
  cacheResults?: boolean;
  /**
   * Hard country filter (ISO 3166-1 alpha-2). Defaults to the organization's
   * country for the final geocode and to NO filter for as-you-type
   * suggestions, which use a soft location bias instead — see
   * `azureMapsProxy.suggestAddresses`. Pass this only to override both.
   */
  defaultCountry?: string[];
  /** Suggestions also return points of interest (businesses, venues). */
  includePoi?: boolean;
  /** Suggestions per query (default 5). */
  suggestionLimit?: number;
}

/**
 * The Search Address confidence for a suggestion the user picked, or
 * `undefined` when Azure does not resolve the suggestion's own address back
 * to the same place (then no confidence is stored — never another place's).
 */
async function rescoreSuggestion(
  address: AzureAddress,
  placeId: string | undefined,
): Promise<number | undefined> {
  const query = address.formattedAddress?.trim();
  if (!query) return undefined;
  const country = address.countryRegionISO2?.trim().toUpperCase();
  const [best] = await azureMapsProxy.searchAddress(query, {
    countrySet: country ? [country] : undefined,
    limit: 1,
  });
  if (!best) return undefined;
  const samePlace =
    (placeId && best.id === placeId) ||
    (best.address.formattedAddress?.trim().toLowerCase() === query.toLowerCase());
  return samePlace ? best.score : undefined;
}

/**
 * Below this Search Address score a match is not "valid" (the threshold in
 * `azureMapsProxy.validateAddress`), and a country-restricted search is
 * retried worldwide so a foreign address still resolves.
 */
const VALID_MATCH_SCORE = 0.7;

/**
 * Where a `confidence` figure came from. `match` is Azure's bounded Search
 * Address confidence — the only kind `gis_locations.confidence_score` may
 * hold. `rank` is the typeahead relevance rank from Search Fuzzy: fine for
 * ordering a suggestion list, meaningless as a stored confidence, so
 * `saveLocation` re-scores a `rank` before it writes.
 */
export type ConfidenceSource = 'match' | 'rank';

interface AddressSearchResult {
  address: AzureAddress;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  confidence: number;
  confidenceSource: ConfidenceSource;
  placeId?: string;
  /** Set when the suggestion is a point of interest rather than an address. */
  poi?: GeocodeResult['poi'];
}

export function useAddressLookup(options: UseAddressLookupOptions = {}) {
  const { selectedOrganization } = useOrganization();
  const { i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  // One request per pause in typing, not per keystroke.
  const debouncedQuery = useDebounce(searchQuery.trim(), 250);

  // The organization's country is what makes "1015 CJ 123" resolve to
  // Amsterdam instead of Tijuana: Azure searches the whole world unless told
  // otherwise. `organizations.country` is nullable, so every use below
  // tolerates its absence (and falls back to the old worldwide behaviour).
  const orgCountry = useMemo(() => {
    const code = selectedOrganization?.country?.trim().toUpperCase();
    return code && isSupportedCountryCode(code) ? code : undefined;
  }, [selectedOrganization?.country]);
  const explicitCountry = options.defaultCountry?.length ? options.defaultCountry : undefined;
  // Final geocode: a hard country filter (retried worldwide when the match is
  // weak). The score it returns is stored as `confidence_score`, so it gets
  // NO location bias — Azure blends proximity into the score, and a correct
  // Groningen address would come back at 0.80 instead of 1.00.
  const geocodeCountrySet = explicitCountry ?? (orgCountry ? [orgCountry] : undefined);
  // Suggestions: the opposite — a soft bias and no filter, see the query below.
  const bias = orgCountry ? COUNTRY_SEARCH_BIAS[orgCountry] : undefined;

  /**
   * Validate an address and get corrections
   */
  const validateAddressMutation = useMutation({
    mutationFn: async (address: string) => {
      const searchOptions: SearchAddressOptions = {
        countrySet: geocodeCountrySet,
        widenBelowScore: VALID_MATCH_SCORE,
        limit: 1,
      };

      return await azureMapsProxy.validateAddress(address, searchOptions);
    },
  });

  /**
   * Search for addresses (autocomplete style)
   */
  const searchAddresses = useQuery({
    queryKey: ['addressSearch', debouncedQuery, explicitCountry, orgCountry, options.includePoi, options.suggestionLimit],
    queryFn: async () => {
      if (!debouncedQuery || debouncedQuery.length < 3) {
        return [];
      }

      // Bias, not filter: an NL org typing a Brussels address still gets it.
      const results = await azureMapsProxy.suggestAddresses(debouncedQuery, {
        countrySet: explicitCountry,
        ...bias,
        limit: options.suggestionLimit ?? 5,
        includePoi: options.includePoi,
      });

      return results.map(result => ({
        address: result.address,
        coordinates: {
          latitude: result.position.lat,
          longitude: result.position.lon,
        },
        confidence: result.score,
        confidenceSource: 'rank',
        placeId: result.id,
        ...(result.poi ? { poi: result.poi } : {}),
      } as AddressSearchResult));
    },
    enabled: debouncedQuery.length >= 3,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  /**
   * Geocode a specific address
   */
  const geocodeAddressMutation = useMutation({
    mutationFn: async (address: string) => {
      const results = await azureMapsProxy.geocodeAddress(address, {
        countrySet: geocodeCountrySet,
        widenBelowScore: VALID_MATCH_SCORE,
        limit: 5, // Get up to 5 suggestions
      });

      return results; // Return all results instead of just the first one
    },
  });

  /**
   * Reverse geocode coordinates to address
   */
  const reverseGeocodeMutation = useMutation({
    mutationFn: async ({ lat, lon }: { lat: number; lon: number }) => {
      return await azureMapsProxy.reverseGeocode(lat, lon);
    },
  });

  /**
   * Save a validated address to the database
   */
  const saveLocationMutation = useMutation({
    mutationFn: async (location: {
      address: AzureAddress;
      coordinates: { latitude: number; longitude: number };
      originalInput?: string;
      corrections?: AddressValidation['corrections'];
      confidence?: number;
      /** Defaults to `match`. Pass a suggestion's `confidenceSource` through. */
      confidenceSource?: ConfidenceSource;
      placeId?: string;
      /** Drawn GeoJSON (pin or area) → `gis_locations.geom`; see MapboxLocationPicker `allowArea`. */
      geom?: Geometry;
    }) => {
      if (!selectedOrganization?.id) {
        throw new Error('No organization selected');
      }

      // Resolve-or-create, not blind insert. `uq_gis_locations_org_azure_place`
      // (migration 20260819150100) makes (organization_id, azure_place_id)
      // unique, so a plain insert 23505s the second time the SAME Azure result
      // is saved in an org — which is routine: two people at one office, a
      // business and a place at one address, re-picking the current address
      // while editing, or hand-picking an address the ESPM import already wrote.
      //
      // PostgREST's .upsert({ onConflict }) can NOT be used: it emits
      // `ON CONFLICT (cols)` with no WHERE, which Postgres will not match to a
      // PARTIAL index. Hence select-then-insert, with a 23505 fallback that
      // re-selects — that closes the window between the two statements.
      //
      // A drawn geometry is never reused or matched: it is always its own row,
      // so redrawing one place's shape can't move another entity's address.
      if (location.placeId && !location.geom) {
        const { data: existing } = await supabase
          .from('gis_locations')
          .select('id')
          .eq('organization_id', selectedOrganization.id)
          .eq('azure_place_id', location.placeId)
          .limit(1)
          .maybeSingle();
        if (existing?.id) return existing.id;
      }

      // A picked suggestion carries the fuzzy typeahead RANK, not a match
      // confidence (see `ConfidenceSource`). Re-score it with one Search
      // Address call — the bounded, unbiased figure the column is defined
      // as — and store nothing rather than a number that means something
      // else. Only after the reuse check above, so a re-pick costs no call.
      const confidence =
        location.confidenceSource === 'rank'
          ? await rescoreSuggestion(location.address, location.placeId)
          : location.confidence;

      // Azure hands the number and name over separately; only a caller that
      // built an AzureAddress by hand (a single `addressLine`) needs the
      // country-aware split.
      const street =
        location.address.streetNumber || location.address.streetName
          ? { streetNumber: location.address.streetNumber ?? '', streetName: location.address.streetName ?? '' }
          : splitStreetLine(location.address.addressLine, location.address.countryRegionISO2);

      const { data, error } = await supabase
        .from('gis_locations')
        .insert({
          formatted_address: location.address.formattedAddress || '',
          street_number: street.streetNumber || null,
          street_name: street.streetName || null,
          city: location.address.locality,
          state_province: location.address.adminDistrict,
          postal_code: location.address.postalCode,
          country: location.address.countryRegion,
          country_code: location.address.countryRegionISO2,
          latitude: location.coordinates.latitude,
          longitude: location.coordinates.longitude,
          // A drawn row is never an Azure place (it would collide with the
          // address row on uq_gis_locations_org_azure_place).
          azure_place_id: location.geom ? null : location.placeId,
          confidence_score: confidence ?? null,
          is_validated: true,
          original_input: location.originalInput,
          corrections_applied: location.corrections,
          organization_id: selectedOrganization.id,
          last_verified_at: new Date().toISOString(),
          // PostGIS parses the GeoJSON object (docs/LOCATIONS.md "Drawn geometry").
          geom: location.geom ?? null,
        })
        .select()
        .single();

      if (error) {
        // Lost the race against a concurrent save of the same address — the
        // row now exists, so return it rather than failing the user's action.
        if (error.code === '23505' && location.placeId && !location.geom) {
          const { data: raced } = await supabase
            .from('gis_locations')
            .select('id')
            .eq('organization_id', selectedOrganization.id)
            .eq('azure_place_id', location.placeId)
            .limit(1)
            .maybeSingle();
          if (raced?.id) return raced.id;
        }
        throw error;
      }
      return data.id;
    },
  });

  /**
   * Find nearby locations
   */
  const findNearbyMutation = useMutation({
    mutationFn: async ({
      latitude,
      longitude,
      radiusMeters = 5000,
    }: {
      latitude: number;
      longitude: number;
      radiusMeters?: number;
    }) => {
      const { data, error } = await supabase.rpc('find_nearby_locations', {
        p_latitude: latitude,
        p_longitude: longitude,
        p_radius_meters: radiusMeters,
        p_organization_id: selectedOrganization?.id,
      });

      if (error) throw error;
      return data;
    },
  });

  /**
   * Batch validate multiple addresses
   */
  const batchValidateMutation = useMutation({
    mutationFn: async (addresses: string[]) => {
      const batchResponse = await azureMapsProxy.batchGeocode(addresses);

      return batchResponse.batchItems.map((item, index) => {
        if (item.statusCode === 200 && item.response?.results?.[0]) {
          const result = item.response.results[0];
          return {
            originalInput: addresses[index],
            isValid: result.score > 0.7,
            confidence: result.score,
            address: result.address,
            coordinates: {
              latitude: result.position.lat,
              longitude: result.position.lon,
            },
          };
        }

        return {
          originalInput: addresses[index],
          isValid: false,
          confidence: 0,
          error: item.error?.message,
        };
      });
    },
  });

  // One-line address in the address's own country convention; the country
  // name is appended only when it differs from the organization's.
  const formatAddress = useCallback(
    (address: AzureAddress | undefined): string =>
      formatPostalAddress(address, { homeCountry: orgCountry, locale: i18n.language }),
    [orgCountry, i18n.language],
  );

  // Calculate distance between two points
  const calculateDistance = useCallback(
    (lat1: number, lon1: number, lat2: number, lon2: number) => {
      return azureMapsProxy.calculateDistance(lat1, lon1, lat2, lon2);
    },
    []
  );

  return {
    // Mutations
    validateAddress: validateAddressMutation.mutateAsync,
    geocodeAddress: geocodeAddressMutation.mutateAsync,
    reverseGeocode: reverseGeocodeMutation.mutateAsync,
    saveLocation: saveLocationMutation.mutateAsync,
    findNearby: findNearbyMutation.mutateAsync,
    batchValidate: batchValidateMutation.mutateAsync,

    // Query results
    searchResults: searchAddresses.data || [],
    // "Searching" covers the debounce window too, so the spinner appears on
    // the first keystroke rather than 250ms later.
    isSearching:
      searchAddresses.isFetching ||
      (searchQuery.trim().length >= 3 && searchQuery.trim() !== debouncedQuery),
    setSearchQuery,

    // Loading states
    isValidating: validateAddressMutation.isPending,
    isGeocoding: geocodeAddressMutation.isPending,
    isSaving: saveLocationMutation.isPending,

    // Utilities
    formatAddress,
    calculateDistance,

    // Errors
    validationError: validateAddressMutation.error,
    geocodeError: geocodeAddressMutation.error,
    saveError: saveLocationMutation.error,
  };
}
